import { useNavigation, NavigationProp, CommonActions } from '@react-navigation/native';
import React, { useEffect } from 'react';
import { Alert } from 'react-native';

import { track } from '../../lib/analytics';
import { supabase } from '../../lib/supabase';
import { useAppContext } from '../contexts/AppContext';
import { authService } from '../services/authService';
import { getInitialURL, addLinkingListener, parseInviteLink, type InviteLinkParams } from '../utils/inviteLink';
import { redactUrl } from '../utils/redactUrl';
import { uiText } from '../utils/uiText';

interface DeepLinkHandlerProps {
  children: React.ReactNode;
}

type DeepLinkNavigationParamList = {
  Home: undefined;
  Auth:
    | {
        screen?: 'SignUp';
        params?: {
          referralCode?: string;
        };
      }
    | undefined;
  QuizLanding:
    | {
        sessionId?: string;
        token?: string;
        lang?: string;
      }
    | undefined;
  RoomInviteLanding:
    | {
        roomId: string;
        code?: string;
      }
    | undefined;
  FamiliesAndFriendsHub:
    | {
        screen: string;
        params: {
          roomId: string;
          joinCode: string;
        };
      }
    | undefined;
};

const DeepLinkHandler: React.FC<DeepLinkHandlerProps> = ({ children }) => {
  const navigation = useNavigation<NavigationProp<DeepLinkNavigationParamList>>();
  const { state } = useAppContext();

  useEffect(() => {
    const handleDeepLink = async (url: string) => {
      try {
        // Ignore development server URLs and web navigation URLs
        if (url.startsWith('http://localhost:') || url.startsWith('https://localhost:')) {
          // These are just web navigation URLs from the development server, not deep links
          return;
        }

        console.log('🔗 [DeepLink] Deep link received:', redactUrl(url));

        // Check if this is an OAuth callback URL with tokens
        if (url.includes('/auth/callback') || url.includes('access_token=')) {
          console.log('🔐 [OAuth] OAuth callback URL detected, processing tokens...');
          await handleOAuthCallback(url);
          return;
        }

        // Check if this is an OAuth code callback.
        // Real invite links also use ?code=, so only treat non-app deep links as OAuth.
        if (
          url.includes('?code=') &&
          !url.includes('join') &&
          !url.startsWith('kingdomquiz://') &&
          !url.startsWith('kingdombiblequiz://')
        ) {
          console.log('🔐 [OAuth] OAuth code callback detected, processing...');
          await handleOAuthCallback(url);
          return;
        }

        // Only process actual deep links (app schemes or production URLs)
        if (
          !url.startsWith('kingdomquiz://') &&
          !url.startsWith('kingdombiblequiz://') &&
          !url.includes('kingdombiblequiz.com')
        ) {
          // Not a deep link we should handle
          return;
        }
        
        const linkParams = await parseInviteLink(url);
        
        if (!linkParams) {
          console.warn('Invalid deep link format:', redactUrl(url));
          return;
        }
        
        // Track the deep link in analytics
        track('deep_link_opened', {
          type: linkParams.type,
          url,
          user_authenticated: !!state.user
        });
        
        // Handle the deep link based on type
        handleDeepLinkNavigation(linkParams);
      } catch (error: unknown) {
        console.error('Failed to handle deep link:', error);
      }
    };
    
    // Check for initial URL (app opened via link)
    const checkInitialURL = async () => {
      const url = await getInitialURL();
      if (url) {
        // Handle immediately - navigation should be ready by the time this component mounts
        handleDeepLink(url);
      }
    };
    
    checkInitialURL();
    
    // Add listener for future deep links
    const unsubscribe = addLinkingListener(handleDeepLink);
    
    return () => {
      unsubscribe();
    };
  }, [state.user]);

  const handleOAuthCallback = async (url: string) => {
    try {
      console.log('🔐 [OAuth] Processing OAuth callback URL:', redactUrl(url));

      // Parse URL to extract tokens or auth code
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search || urlObj.hash.replace('#', ''));

      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const code = params.get('code');
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      // Handle OAuth errors
      if (error) {
        console.error('❌ [OAuth] OAuth error:', error, errorDescription);
        Alert.alert(uiText('signInFailed'), errorDescription || error);
        return;
      }

      // If we have tokens directly, set the session
      if (accessToken && refreshToken) {
        console.log('🔐 [OAuth] Setting session with tokens from deep link');

        const { data, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });

        if (sessionError) {
          console.error('❌ [OAuth] Session error:', sessionError);
          // Use setTimeout to ensure this is the only alert shown
          setTimeout(() => {
            Alert.alert(uiText('signInFailed'), uiText('signInFailedMessage'));
          }, 100);
          return;
        }

        console.log('✅ [OAuth] Session established successfully');

        if (data.session?.user) {
          await authService.createOrUpdateOAuthProfile(data.session.user);
        }

        // Track successful OAuth sign in
        track('oauth_sign_in_success', {
          user_id: data.session?.user?.id,
          email: data.session?.user?.email
        });

        // Navigate to home or main screen WITHOUT showing success alert
        // The app will update naturally when auth state changes
        if (navigation) {
          setTimeout(() => {
            navigation.dispatch(
              CommonActions.reset({
                index: 0,
                routes: [{ name: 'Home' }]
              })
            );
          }, 100);
        }

        return;
      }

      // If we have an auth code, exchange it for a session
      if (code) {
        console.log('🔐 [OAuth] Exchanging authorization code for session');

        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

        if (exchangeError) {
          console.error('❌ [OAuth] Code exchange error:', exchangeError);
          // Use setTimeout to ensure this is the only alert shown
          setTimeout(() => {
            Alert.alert(uiText('signInFailed'), uiText('signInFailedMessage'));
          }, 100);
          return;
        }

        console.log('✅ [OAuth] Session established from code exchange');

        if (data.session?.user) {
          await authService.createOrUpdateOAuthProfile(data.session.user);
        }

        // Track successful OAuth sign in
        track('oauth_sign_in_success', {
          user_id: data.session?.user?.id,
          email: data.session?.user?.email
        });

        // Navigate to home or main screen WITHOUT showing success alert
        // The app will update naturally when auth state changes
        if (navigation) {
          setTimeout(() => {
            navigation.dispatch(
              CommonActions.reset({
                index: 0,
                routes: [{ name: 'Home' }]
              })
            );
          }, 100);
        }

        return;
      }

      console.warn('⚠️ [OAuth] No tokens or code found in callback URL');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An error occurred during sign in.';
      console.error('❌ [OAuth] Error processing OAuth callback:', error);
      Alert.alert(uiText('signInError'), message);
    }
  };

  const handleDeepLinkNavigation = (linkParams: InviteLinkParams) => {
    const { type, id, code, referrer, sessionId, token, lang } = linkParams;
    
    if (!navigation) {
      console.warn('Navigation not available for deep link');
      return;
    }
    
    switch (type) {
      case 'room':
        handleRoomInvite(id, code);
        break;
        
      case 'event':
        handleEventInvite(id, code);
        break;
        
      case 'referral':
        handleReferralLink(code, referrer);
        break;

      case 'quiz':
        handleQuizLink(sessionId, token, lang);
        break;
        
      default:
        console.warn('Unknown deep link type:', type);
    }
  };
  
  const handleQuizLink = (quizSessionId?: string, accessToken?: string, language?: string) => {
    if (!quizSessionId) {
      console.warn('Missing session ID for quiz deep link');
      Alert.alert(uiText('quizUnavailable'), uiText('quizLinkInvalid'));
      return;
    }

    try {
      navigation.navigate('QuizLanding', {
        sessionId: quizSessionId,
        token: accessToken,
        lang: language,
      });

      track('deep_link_quiz_open', {
        session_id: quizSessionId,
        has_access_token: !!accessToken,
        user_id: state.user?.id,
      });
    } catch (error: unknown) {
      console.error('Navigation failed:', error);
      Alert.alert(uiText('navigationError'), uiText('openQuizFailed'));
    }
  };

  const handleRoomInvite = (roomId: string, joinCode: string) => {
    if (!roomId || !joinCode) {
      Alert.alert(uiText('invalidLink'), uiText('roomInviteInvalid'));
      return;
    }

    try {
      navigation.navigate('RoomInviteLanding', {
        roomId,
        code: joinCode,
      });
    } catch (error: unknown) {
      console.error('Navigation failed:', error);
      Alert.alert(uiText('navigationError'), uiText('openRoomFailed'));
    }
    
    // Track in analytics
    track('deep_link_room_join_attempt', { 
      room_id: roomId,
      user_id: state.user?.id
    });
  };
  
  const handleEventInvite = (eventId: string, joinCode: string) => {
    if (!eventId || !joinCode) {
      Alert.alert(uiText('invalidLink'), uiText('eventInviteInvalid'));
      return;
    }

    Alert.alert(
      uiText('eventLinkUnavailable'),
      uiText('eventLinksUnsupported')
    );

    track('deep_link_event_unsupported', {
      event_id: eventId,
      user_id: state.user?.id
    });
  };
  
  const handleReferralLink = (referralCode: string, referrerId: string) => {
    if (!referralCode || !referrerId) {
      Alert.alert(uiText('invalidLink'), uiText('referralInvalid'));
      return;
    }
    
    if (state.user) {
      Alert.alert(
        uiText('alreadyRegistered'),
        uiText('alreadyRegisteredMessage'),
        [{ text: uiText('ok') }]
      );
      return;
    }
    
    // Store referral info for signup
    Alert.alert(
      uiText('joinKbqTitle'),
      uiText('joinKbqMessage'),
      [
        { text: uiText('maybeLater'), style: 'cancel' },
        { 
          text: uiText('signUp'), 
          onPress: () => {
            // Navigate to signup with referral code
            try {
              navigation.navigate('Auth', {
                screen: 'SignUp',
                params: { referralCode }
              });
            } catch (error: unknown) {
              console.error('Navigation failed:', error);
              Alert.alert(uiText('navigationError'), uiText('signupNavFailed'));
            }
          }
        }
      ]
    );
    
    // Track in analytics
    track('deep_link_referral_received', { 
      referral_code: referralCode,
      referrer_id: referrerId
    });
  };

  return <>{children}</>;
};

export default DeepLinkHandler;
